import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { CheckCircle } from 'lucide-react';
import { registerCompany, verifyVatNumber } from '../services/api';
import i18n from '../i18n';

// Stili CSS per i cursori
const modalStyles = `
.custom-cursor-modal input[type="text"],
.custom-cursor-modal input[type="email"],
.custom-cursor-modal input[type="password"],
.custom-cursor-modal input[type="tel"],
.custom-cursor-modal textarea {
    cursor: text !important;
}

.custom-cursor-modal button,
.custom-cursor-modal .btn {
    cursor: pointer !important;
}

.custom-cursor-modal input[type="checkbox"] {
    cursor: pointer !important;
}

.custom-cursor-modal .form-check-label {
    cursor: pointer !important;
}

.custom-cursor-modal a {
    cursor: pointer !important;
}
`;

// Componente per iniettare gli stili
const StyleInjector = () => {
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.innerHTML = modalStyles;
        document.head.appendChild(styleElement);
        
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);
    
    return null;
};

const CompanyRegisterModal = ({ show, onHide, onSwitchToLogin }) => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        companyName: '',
        vatNumber: '',
        address: '',
        phone: '',
        description: '',
        acceptTerms: false
    });
    
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [vatVerificationStatus, setVatVerificationStatus] = useState(null);
    const [vatVerificationMessage, setVatVerificationMessage] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        console.log('📝 Campo modificato:', name, '=', newValue);
        
        setFormData(prev => {
            const newState = {
                ...prev,
                [name]: newValue
            };
            console.log('🔄 Nuovo stato formData:', newState);
            return newState;
        });
        
        if (name === 'vatNumber') {
            setVatVerificationStatus(null);
            setVatVerificationMessage('');
        }
    };
    
    const validateForm = () => {
        if (formData.password !== formData.confirmPassword) {
            setError("Le password non corrispondono");
            return false;
        }
        
        if (!isValidVatOrFiscalCode(formData.vatNumber)) {
            setError("Inserisci una Partita IVA valida (11 cifre) o un Codice Fiscale di associazione valido (16 caratteri)");
            return false;
        }
        
        if (!formData.acceptTerms) {
            setError("Devi accettare i Termini e Condizioni e l'Informativa sulla Privacy per procedere");
            return false;
        }

        if (!selectedPlan) {
            setError("Seleziona un piano di abbonamento per procedere");
            return false;
        }

        return true;
    };

    const isValidVatOrFiscalCode = (code) => {
        if (!code) return false;
        const vatPattern = /^\d{11}$/;
        const fiscalCodePattern = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i;
        return vatPattern.test(code) || fiscalCodePattern.test(code.toUpperCase());
    };
    
    const handleVerifyVat = async () => {
        console.log('🔍 Verifying VAT number:', formData.vatNumber);
        
        if (!isValidVatOrFiscalCode(formData.vatNumber)) {
            setVatVerificationStatus('invalid');
            setVatVerificationMessage('Inserisci una Partita IVA valida (11 cifre) o un Codice Fiscale di associazione valido (16 caratteri)');
            return;
        }
        
        setVatVerificationStatus('verifying');
        setVatVerificationMessage('Verifica in corso...');
        setError(null);
        
        try {
            console.log('📤 Sending verification request for:', formData.vatNumber);
            const response = await verifyVatNumber(formData.vatNumber);
            console.log('✅ Verification response:', response);
            
            if (response.valid) {
                setVatVerificationStatus('valid');
                setVatVerificationMessage(`${response.type} verificato con successo`);
                console.log('✅ VAT verified successfully');
            } else {
                setVatVerificationStatus('invalid');
                setVatVerificationMessage(response.message || 'Codice non valido');
                console.log('❌ VAT verification failed:', response.message);
            }
        } catch (error) {
            console.error('❌ Error verifying VAT:', error);
            setVatVerificationStatus('invalid');
            setVatVerificationMessage('Errore durante la verifica del codice');
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const form = e.currentTarget;
        if (form.checkValidity() === false || !validateForm()) {
            e.stopPropagation();
            setValidated(true);
            return;
        }
        
        setIsLoading(true);
        setError(null);
        setSuccess(false);
        
        try {
            console.log("Invio dati di registrazione azienda:", formData);
            
            const companyData = {
                username: formData.username,
                email: formData.email,
                password: formData.password,
                companyName: formData.companyName,
                vatNumber: formData.vatNumber,
                address: formData.address || '',
                phone: formData.phone || '',
                description: formData.description || ''
            };
            
            const response = await registerCompany(companyData);
            console.log("Risposta registrazione azienda:", response);
            
            setSuccess(true);
            setFormData({
                username: '',
                email: '',
                password: '',
                confirmPassword: '',
                companyName: '',
                vatNumber: '',
                address: '',
                phone: '',
                description: '',
                acceptTerms: false
            });
            
            setTimeout(() => {
                if (onSwitchToLogin) {
                    onHide();
                    onSwitchToLogin();
                }
            }, 2000);
        } catch (error) {
            console.error('Errore durante la registrazione:', error);
            
            let errorMessage = 'Si è verificato un errore durante la registrazione';
            
            if (error.response && error.response.data) {
                if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                } else if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };
    
    const getVatVerificationColor = () => {
        switch (vatVerificationStatus) {
            case 'valid': return 'text-success';
            case 'invalid': return 'text-danger';
            case 'verifying': return 'text-warning';
            default: return '';
        }
    };
    
    const openTerms = (e) => {
        e.preventDefault();
        window.open('/terms', '_blank');
    };
    
    const openPrivacy = (e) => {
        e.preventDefault();
        window.open('/privacy', '_blank');
    };
    
    // Log per debug dello stato
    useEffect(() => {
        console.log('🔍 Stato acceptTerms:', formData.acceptTerms);
    }, [formData.acceptTerms]);

    return (
        <>
            <StyleInjector />
            <Modal
                show={show}
                onHide={onHide}
                size="lg"
                backdrop="static"
                keyboard={false}
                scrollable
                className="custom-cursor-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{i18n.language === 'en' ? 'Company Registration' : 'Registrazione Azienda'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{i18n.language === 'en' ? 'Registration completed successfully! You will be redirected to login.' : 'Registrazione completata con successo! Verrai reindirizzato al login.'}</Alert>}
                    
                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Username *</Form.Label>
                            <Form.Control
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                required
                                disabled={isLoading}
                                autoComplete="username"
                            />
                            <Form.Control.Feedback type="invalid">
                                Inserisci un nome utente
                            </Form.Control.Feedback>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                disabled={isLoading}
                                autoComplete="email"
                            />
                            <Form.Control.Feedback type="invalid">
                                Inserisci un indirizzo email valido
                            </Form.Control.Feedback>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Password *</Form.Label>
                            <Form.Control
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                disabled={isLoading}
                                minLength={8}
                                autoComplete="new-password"
                            />
                            <Form.Control.Feedback type="invalid">
                                La password deve contenere almeno 8 caratteri
                            </Form.Control.Feedback>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Conferma Password *</Form.Label>
                            <Form.Control
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                                disabled={isLoading}
                                autoComplete="new-password"
                            />
                            <Form.Control.Feedback type="invalid">
                                Le password devono corrispondere
                            </Form.Control.Feedback>
                        </Form.Group>
                        
                        <hr className="my-4" />
                        <h5>{i18n.language === 'en' ? 'Company Information' : 'Informazioni Azienda'}</h5>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>{i18n.language === 'en' ? 'Company Name *' : 'Nome Azienda *'}</Form.Label>
                            <Form.Control
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                required
                                disabled={isLoading}
                                autoComplete="organization"
                            />
                            <Form.Control.Feedback type="invalid">
                                Inserisci il nome dell'azienda
                            </Form.Control.Feedback>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>{i18n.language === 'en' ? 'VAT Number or Tax Code *' : 'Partita IVA o Codice Fiscale *'}</Form.Label>
                            <div className="input-group">
                                <Form.Control
                                    type="text"
                                    name="vatNumber"
                                    value={formData.vatNumber}
                                    onChange={handleChange}
                                    placeholder="Inserisci P.IVA (11 cifre) o Codice Fiscale (16 caratteri)"
                                    required
                                    disabled={isLoading}
                                    isInvalid={validated && !formData.vatNumber}
                                    style={{
                                        borderColor: vatVerificationStatus === 'valid' ? '#28a745' : 
                                                   vatVerificationStatus === 'invalid' ? '#dc3545' : '',
                                        cursor: 'text'
                                    }}
                                />
                                <Button 
                                    variant={vatVerificationStatus === 'valid' ? 'success' : 'outline-secondary'}
                                    onClick={handleVerifyVat}
                                    disabled={!formData.vatNumber || vatVerificationStatus === 'verifying' || isLoading}
                                    type="button"
                                    style={{ cursor: 'pointer' }}
                                >
                                    {vatVerificationStatus === 'verifying' ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-1"
                                            />
                                            Verifica...
                                        </>
                                    ) : vatVerificationStatus === 'valid' ? (
                                        <>
                                            <CheckCircle className="me-1" />
                                            Verificato
                                        </>
                                    ) : (
                                        'Verifica'
                                    )}
                                </Button>
                            </div>
                            <Form.Text className="text-muted">
                                Inserisci la Partita IVA della tua azienda o il Codice Fiscale della tua associazione
                            </Form.Text>
                            {vatVerificationMessage && (
                                <div className={`small mt-1 ${getVatVerificationColor()}`}>
                                    {vatVerificationMessage}
                                </div>
                            )}
                            {formData.vatNumber && vatVerificationStatus !== 'valid' && (
                                <div className="small mt-1 text-warning">
                                    ⚠️ Clicca su "Verifica" per validare il codice
                                </div>
                            )}
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>{i18n.language === 'en' ? 'Address *' : 'Indirizzo *'}</Form.Label>
                            <Form.Control
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                required
                                disabled={isLoading}
                                autoComplete="street-address"
                            />
                            <Form.Control.Feedback type="invalid">
                                Inserisci l'indirizzo dell'azienda
                            </Form.Control.Feedback>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>{i18n.language === 'en' ? 'Phone' : 'Telefono'}</Form.Label>
                            <Form.Control
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                disabled={isLoading}
                                autoComplete="tel"
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>{i18n.language === 'en' ? 'Description' : 'Descrizione'}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Descrivi brevemente la tua azienda..."
                                disabled={isLoading}
                                style={{ cursor: 'text' }}
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="acceptTerms"
                                    name="acceptTerms"
                                    checked={formData.acceptTerms}
                                    onChange={(e) => {
                                        console.log('✅ Checkbox cliccata!', e.target.checked);
                                        handleChange(e);
                                    }}
                                    required
                                    disabled={isLoading}
                                />
                                <label className="form-check-label" htmlFor="acceptTerms">
                                    {i18n.language === 'en' ? 'I accept the ' : 'Accetto i '}<button type="button" className="btn btn-link p-0 align-baseline" style={{ textDecoration: 'underline' }} onClick={openTerms}>{i18n.language === 'en' ? 'Terms and Conditions' : 'Termini e Condizioni'}</button>{i18n.language === 'en' ? ' and the ' : " e l'"}<button type="button" className="btn btn-link p-0 align-baseline" style={{ textDecoration: 'underline' }} onClick={openPrivacy}>{i18n.language === 'en' ? 'Privacy Policy' : 'Informativa sulla Privacy'}</button>
                                </label>
                                {validated && !formData.acceptTerms && (
                                    <div className="invalid-feedback d-block">
                                        Devi accettare i Termini e Condizioni e l'Informativa sulla Privacy per procedere
                                    </div>
                                )}
                            </div>
                        </Form.Group>
                        
                        {/* Selezione piano */}
                        <Form.Group className="mb-3">
                            <Form.Label><strong>{i18n.language === 'en' ? 'Subscription plan *' : 'Piano di abbonamento *'}</strong></Form.Label>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                                {[
                                    { key: 'base', label: 'Base', price: i18n.language === 'en' ? 'Free' : 'Gratuito', features: i18n.language === 'en' ? '1 event/month, basic analytics' : '1 evento/mese, analytics base', color: '#6366f1' },
                                    { key: 'pro', label: 'Pro', price: '€29/mo', features: i18n.language === 'en' ? '10 events/month, advanced analytics' : '10 eventi/mese, analytics avanzate', color: 'var(--primary)', recommended: true },
                                    { key: 'business', label: 'Business', price: '€79/mo', features: i18n.language === 'en' ? 'Unlimited, priority support' : 'Illimitato, supporto prioritario', color: 'var(--secondary)' },
                                ].map(plan => (
                                    <div
                                        key={plan.key}
                                        onClick={() => setSelectedPlan(plan.key)}
                                        style={{
                                            flex: '1', minWidth: '140px', padding: '1rem', borderRadius: '10px', cursor: 'pointer',
                                            border: `2px solid ${selectedPlan === plan.key ? plan.color : '#e0e0e0'}`,
                                            background: selectedPlan === plan.key ? `${plan.color}10` : '#fafafa',
                                            transition: 'all 0.15s', position: 'relative',
                                        }}
                                    >
                                        {plan.recommended && (
                                            <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: plan.color, color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                                                {i18n.language === 'en' ? 'RECOMMENDED' : 'CONSIGLIATO'}
                                            </div>
                                        )}
                                        <div style={{ fontWeight: 700, color: plan.color }}>{plan.label}</div>
                                        <div style={{ fontWeight: 800, fontSize: '1.1rem', margin: '0.25rem 0' }}>{plan.price}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#666' }}>{plan.features}</div>
                                    </div>
                                ))}
                            </div>
                            {validated && !selectedPlan && (
                                <div className="text-danger small mt-1">Seleziona un piano per procedere</div>
                            )}
                        </Form.Group>

                        <div className="d-grid gap-2">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isLoading || !formData.acceptTerms}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        {i18n.language === 'en' ? 'Registering...' : 'Registrazione in corso...'}
                                    </>
                                ) : (
                                    i18n.language === 'en' ? 'Register Company' : 'Registra Azienda'
                                )}
                            </Button>
                            
                            {!isLoading && (
                                <div className="text-center small">
                                    {formData.acceptTerms ? (
                                        <div className="text-success mb-1">
                                            ✅ Tutto pronto! Clicca per registrare la tua azienda
                                        </div>
                                    ) : (
                                        <div className="text-danger mb-1">
                                            ⚠️ Accetta i termini e condizioni per procedere
                                        </div>
                                    )}
                                    <div className="text-muted mt-2">
                                        Stato: Termini {formData.acceptTerms ? '✅' : '❌'}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <div className="w-100 text-center">
                        <p className="mb-0">
                            {i18n.language === 'en' ? 'Already have an account?' : 'Hai già un account?'} <Button variant="link" className="p-0" onClick={onSwitchToLogin} style={{ cursor: 'pointer' }}>{i18n.language === 'en' ? 'Log in' : 'Accedi'}</Button>
                        </p>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CompanyRegisterModal;